import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, Search, User, Mail, Phone, Shield, RefreshCw,
  LayoutDashboard, FolderKanban, CheckSquare, Calendar, Clock,
  UserCog, Briefcase, Target, BarChart3, Database, Eye,
  Pencil, Trash2, KeyRound, MoreVertical, Share2
} from 'lucide-react';
import { toast } from 'sonner';

const ALL_ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  { value: 'manager', label: 'Manager', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  { value: 'sales_person', label: 'Sales Person', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  { value: 'editor_shooter', label: 'Editor/Shooter', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  { value: 'photographer', label: 'Photographer', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  { value: 'videographer', label: 'Videographer', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  { value: 'social_media', label: 'Social Media', color: 'bg-pink-500/10 text-pink-400 border-pink-500/20' },
  { value: 'freelancer', label: 'Freelancer', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' }
];

const getRoleConfig = (roleValue) => ALL_ROLES.find(r => r.value === roleValue) || { label: roleValue, color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' };

const TeamPage = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('members'); // 'members' | 'alignment'
  const [alignment, setAlignment] = useState([]);
  const [alignmentLoading, setAlignmentLoading] = useState(false);

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditRolesDialog, setShowEditRolesDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  // Create form
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', phone: '', roles: [] });
  // Edit roles form
  const [editRoles, setEditRoles] = useState([]);
  // Password form
  const [newPassword, setNewPassword] = useState('');

  const isAdmin = (user?.roles || [user?.role]).includes('admin');
  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  useEffect(() => { fetchTeam(); }, []);

  useEffect(() => {
    if (activeTab === 'alignment' && isManager) {
      setAlignmentLoading(true);
      api.get('/team/alignment-overview')
        .then(r => setAlignment(r.data || []))
        .catch(() => toast.error('Failed to load alignment'))
        .finally(() => setAlignmentLoading(false));
    }
  }, [activeTab, isManager]);

  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await api.get('/users');
      setTeamMembers(res.data);
    } catch (err) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Name, email & password are required');
      return;
    }
    if (newUser.roles.length === 0) {
      toast.error('Select at least one role');
      return;
    }
    try {
      await api.post('/users', {
        name: newUser.name.trim(),
        email: newUser.email.trim().toLowerCase(),
        password: newUser.password.trim(),
        phone: newUser.phone || null,
        roles: newUser.roles,
        role: newUser.roles[0]
      });
      toast.success('Team member added');
      setShowCreateDialog(false);
      setNewUser({ name: '', email: '', password: '', phone: '', roles: [] });
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleUpdateRoles = async () => {
    if (editRoles.length === 0) {
      toast.error('Select at least one role');
      return;
    }
    try {
      await api.patch(`/users/${selectedMember.id}/roles`, { roles: editRoles });
      toast.success(`Roles updated for ${selectedMember.name}`);
      setShowEditRolesDialog(false);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update roles');
    }
  };

  const handleResetPassword = async () => {
    const trimmedPassword = (newPassword || '').trim();
    if (!trimmedPassword || trimmedPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.patch(`/users/${selectedMember.id}/password`, { password: trimmedPassword });
      toast.success(`Password updated for ${selectedMember.name}`);
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/users/${selectedMember.id}`);
      toast.success(`${selectedMember.name} has been removed`);
      setShowDeleteDialog(false);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditRoles = (member) => {
    setSelectedMember(member);
    setEditRoles(member.roles || [member.role]);
    setShowEditRolesDialog(true);
    setActionMenuOpen(null);
  };

  const openPasswordReset = (member) => {
    setSelectedMember(member);
    setNewPassword('');
    setShowPasswordDialog(true);
    setActionMenuOpen(null);
  };

  const openDeleteConfirm = (member) => {
    setSelectedMember(member);
    setShowDeleteDialog(true);
    setActionMenuOpen(null);
  };

  const toggleNewUserRole = (roleValue) => {
    setNewUser(prev => ({
      ...prev,
      roles: prev.roles.includes(roleValue)
        ? prev.roles.filter(r => r !== roleValue)
        : [...prev.roles, roleValue]
    }));
  };

  const toggleEditRole = (roleValue) => {
    setEditRoles(prev =>
      prev.includes(roleValue)
        ? prev.filter(r => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  const filteredMembers = teamMembers.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         m.email.toLowerCase().includes(searchQuery.toLowerCase());
    const memberRoles = m.roles || [m.role];
    const matchesRole = roleFilter === 'all' || memberRoles.includes(roleFilter);
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Team Management</h1>
          <p className="text-zinc-500 text-sm mt-1">{teamMembers.length} team members</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchTeam}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-team">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          {isManager && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="add-team-member-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Member
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {isManager && (
        <div className="flex gap-1 border-b border-white/10" data-testid="team-tabs">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'members'
                ? 'text-[#F5A623] border-b-2 border-[#F5A623]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-members">Members ({teamMembers.length})</button>
          <button
            onClick={() => setActiveTab('alignment')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'alignment'
                ? 'text-[#F5A623] border-b-2 border-[#F5A623]'
                : 'text-zinc-500 hover:text-white'
            }`}
            data-testid="tab-alignment">Alignment Overview</button>
        </div>
      )}

      {/* Alignment Overview Tab */}
      {isManager && activeTab === 'alignment' && (
        <div className="space-y-3" data-testid="alignment-view">
          {alignmentLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#F5A623]"></div>
            </div>
          ) : alignment.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No team assignments yet.</p>
              <p className="text-xs mt-1">Assign team members to project events from the Project detail page.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500">Upcoming 60 days · {alignment.filter(p => p.has_overlap).length} member(s) with same-day overlaps</p>
              {alignment.map(p => (
                <Card key={p.user_id} className={`bg-[#121212] ${p.has_overlap ? 'border-rose-500/30' : 'border-white/10'}`}
                  data-testid={`align-${p.name}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F5A623] to-orange-500 flex items-center justify-center text-black font-semibold text-xs">
                          {p.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{p.name}</p>
                          <p className="text-[11px] text-zinc-500 capitalize">{(p.roles || []).join(' · ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.has_overlap && (
                          <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-400 border-rose-500/20">⚠ Overlap</Badge>
                        )}
                        <Badge variant="outline" className="text-xs bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20">
                          {p.event_count} event{p.event_count !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>
                    {p.events.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {p.events.slice(0, 10).map((e, i) => {
                          const d = new Date(e.date);
                          return (
                            <Badge key={i} variant="outline"
                              className={`text-[10px] ${
                                e.timing === 'night' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              }`}
                              title={`${e.client_name} — ${e.project_title}`}>
                              {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · {e.event_name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                    {p.events.length === 0 && (
                      <p className="text-[11px] text-zinc-600 mt-2">No shoots in next 60 days</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Members Tab */}
      {(!isManager || activeTab === 'members') && (
      <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
            data-testid="search-team-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#121212] border-white/10 text-white" data-testid="role-filter-select">
            <Shield className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Roles</SelectItem>
            {ALL_ROLES.map(r => (
              <SelectItem key={r.value} value={r.value} className="text-white hover:bg-white/10">{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Team Table */}
      <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="team-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Member</th>
                <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Email</th>
                <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Roles</th>
                <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Phone</th>
                {isManager && (
                  <th className="text-right text-xs text-zinc-400 font-medium px-4 py-3">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map(member => {
                const memberRoles = member.roles || [member.role];
                const isSelf = member.id === user?.id;
                const memberIsAdmin = memberRoles.includes('admin');
                const canManage = isManager && !isSelf && (isAdmin || !memberIsAdmin);

                return (
                  <tr key={member.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`team-row-${member.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-[#F5A623]/20 flex items-center justify-center shrink-0">
                          <User className="h-4 w-4 text-[#F5A623]" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{member.name}</p>
                          {isSelf && <span className="text-[10px] text-[#F5A623]">(You)</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-zinc-400">{member.email}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {memberRoles.map(r => {
                          const cfg = getRoleConfig(r);
                          return (
                            <Badge key={r} variant="outline" className={`text-[10px] ${cfg.color}`} data-testid={`role-badge-${r}`}>
                              {cfg.label}
                            </Badge>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-zinc-400">{member.phone || '—'}</span>
                    </td>
                    {isManager && (
                      <td className="px-4 py-3 text-right">
                        {canManage && (
                          <div className="relative inline-block">
                            <button
                              onClick={(e) => { e.stopPropagation(); setActionMenuOpen(actionMenuOpen === member.id ? null : member.id); }}
                              className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                              data-testid={`member-actions-${member.id}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            {actionMenuOpen === member.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-2xl z-50" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => openEditRoles(member)}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                                  data-testid={`edit-roles-${member.id}`}
                                >
                                  <Shield className="h-3.5 w-3.5" /> Edit Roles
                                </button>
                                <button
                                  onClick={() => openPasswordReset(member)}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                                  data-testid={`reset-password-${member.id}`}
                                >
                                  <KeyRound className="h-3.5 w-3.5" /> Reset Password
                                </button>
                                <button
                                  onClick={() => openDeleteConfirm(member)}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-rose-400 hover:bg-rose-500/10 transition-colors"
                                  data-testid={`delete-user-${member.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Delete Member
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredMembers.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <User className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No team members found</p>
          </div>
        )}
      </div>
      </>
      )}

      {/* === DIALOGS === */}

      {/* Create Member Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription className="text-zinc-400">Create a new account with login credentials.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Full Name *</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="John Doe" data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email *</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="john@wittyshadows.com" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Password *</Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Min 6 characters" data-testid="new-user-password" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Phone</Label>
              <Input value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="+91 9876543210" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Roles * <span className="text-zinc-600 normal-case">(select one or more)</span></Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-user-roles-grid">
                {ALL_ROLES.map(r => (
                  <label key={r.value} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                    newUser.roles.includes(r.value) 
                      ? 'border-[#F5A623]/50 bg-[#F5A623]/10' 
                      : 'border-white/10 hover:border-white/20 bg-[#0A0A0A]'
                  }`}>
                    <Checkbox
                      checked={newUser.roles.includes(r.value)}
                      onCheckedChange={() => toggleNewUserRole(r.value)}
                      className="border-white/20 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]"
                      data-testid={`new-role-${r.value}`}
                    />
                    <span className="text-sm text-zinc-300">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleCreateUser} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-new-user-btn">Add Member</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={showEditRolesDialog} onOpenChange={setShowEditRolesDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Roles — {selectedMember?.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">Assign multiple roles to this team member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-2" data-testid="edit-roles-grid">
              {ALL_ROLES.map(r => (
                <label key={r.value} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                  editRoles.includes(r.value) 
                    ? 'border-[#F5A623]/50 bg-[#F5A623]/10' 
                    : 'border-white/10 hover:border-white/20 bg-[#0A0A0A]'
                }`}>
                  <Checkbox
                    checked={editRoles.includes(r.value)}
                    onCheckedChange={() => toggleEditRole(r.value)}
                    className="border-white/20 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]"
                    data-testid={`edit-role-${r.value}`}
                  />
                  <span className="text-sm text-zinc-300">{r.label}</span>
                </label>
              ))}
            </div>
            {editRoles.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2 bg-[#0A0A0A] rounded-md border border-white/5">
                <span className="text-xs text-zinc-500 mr-1">Selected:</span>
                {editRoles.map(r => {
                  const cfg = getRoleConfig(r);
                  return <Badge key={r} variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>;
                })}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEditRolesDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleUpdateRoles} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="save-roles-btn">Save Roles</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password — {selectedMember?.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">Set a new password for this team member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Min 6 characters"
                data-testid="new-password-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleResetPassword} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="save-password-btn">Update Password</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Team Member</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to remove <span className="text-white font-medium">{selectedMember?.name}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={handleDeleteUser} variant="destructive" className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete Member</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Access Guide */}
      {isManager && <RoleAccessGuide />}
    </div>
  );
};

// ==================== ROLE ACCESS GUIDE ====================

const PANEL_CONFIG = [
  { label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'sales_person', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer'] },
  { label: 'Projects', icon: FolderKanban, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'freelancer'] },
  { label: 'Calendar', icon: Calendar, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'sales_person', 'social_media'] },
  { label: 'Tasks', icon: CheckSquare, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer'] },
  { label: 'Data Management', icon: Database, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer'] },
  { label: 'Accounts', icon: BarChart3, roles: ['admin', 'manager'] },
  { label: 'Team', icon: User, roles: ['admin', 'manager'] },
  { label: 'Attendance', icon: Calendar, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media'] },
  { label: 'Time Tracking', icon: Clock, roles: ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'freelancer'] },
  { label: 'Freelancers', icon: UserCog, roles: ['admin', 'manager'] },
  { label: 'Freelancer Portal', icon: Briefcase, roles: ['freelancer'] },
  { label: 'Leads & Sales', icon: Target, roles: ['admin', 'manager', 'sales_person'] },
  { label: 'Sales Reports', icon: BarChart3, roles: ['admin', 'manager', 'sales_person'] },
];

const ROLE_LIST = ALL_ROLES;

const RoleAccessGuide = () => (
  <div className="mt-8" data-testid="role-access-guide">
    <div className="flex items-center gap-2 mb-4">
      <Eye className="h-5 w-5 text-[#F5A623]" />
      <h2 className="text-lg font-semibold text-white">Role Access Guide</h2>
      <span className="text-xs text-zinc-500">Which panels each role can see</span>
    </div>
    <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3 sticky left-0 bg-[#121212]">Panel</th>
              {ROLE_LIST.map(r => (
                <th key={r.value} className="text-center text-xs text-zinc-400 font-medium px-2 py-3 min-w-[80px]">{r.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PANEL_CONFIG.map(panel => (
              <tr key={panel.label} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <td className="px-4 py-2.5 sticky left-0 bg-[#121212]">
                  <div className="flex items-center gap-2">
                    <panel.icon className="h-4 w-4 text-zinc-500" />
                    <span className="text-sm text-zinc-300">{panel.label}</span>
                  </div>
                </td>
                {ROLE_LIST.map(r => (
                  <td key={r.value} className="text-center px-2 py-2.5">
                    {panel.roles.includes(r.value) ? (
                      <span className="inline-block h-5 w-5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs leading-5">&#10003;</span>
                    ) : (
                      <span className="inline-block h-5 w-5 rounded-full bg-white/5 text-zinc-600 text-xs leading-5">&#8722;</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export default TeamPage;
